import { useAppSelector } from '@/hooks'
import { ID } from '@/types/model'
import { getDateFromTimestamp } from '@/utils/timeUtilities'
import { selectNextWithThermostatId } from '@/selectors/thermostatDemandResponseEvents'
import { thermostatsCollection } from '@/reducers/thermostats'
import { Thermostat, ThermostatDemandResponseEvent } from '@/types'
import { eventIsCurrentlyActive } from '@/types/deviceDemandResponseEvent'

// Backend `cancelled_reason` set when a member's own schedule resumed and opted them out of a DR
// event (smarttherms `ThermostatEventCancelledReason.USER_REMOTE_ADJUSTMENT_SCHEDULE`).
export const SCHEDULE_OPT_OUT_CANCELLED_REASON =
  'user_remote_adjustment_schedule'

export type ScheduleWillOptOutWarningInput = {
  // Brand label for the OEM app, e.g. "Ecobee" / "Honeywell".
  brandLabel: string
  // Formatted opt-out time, e.g. "4:30 PM".
  scheduleOptOutTime: string
}

/**
 * Copy for the *pre-emptive* warning: the member is still enrolled but their schedule is going to
 * opt them out later in the event. Points them to the brand's app to edit the schedule so they
 * stay in *this* event.
 */
export function buildScheduleWillOptOutWarning({
  brandLabel,
  scheduleOptOutTime,
}: ScheduleWillOptOutWarningInput): string {
  return `Your thermostat schedule will opt you out of this event at ${scheduleOptOutTime}. Edit your thermostat's schedule in the ${brandLabel} app to stay in this event.`
}

/**
 * Copy for the *post* opt-out warning: the member's schedule has already opted them out of the
 * current event. Points them to the brand's app to edit the schedule so they stay in *future*
 * events (this one is already lost, though they can still opt back in via the event banner).
 */
export function buildScheduleDidOptOutWarning(brandLabel: string): string {
  return `Your thermostat schedule opted you out of today's event. Edit your thermostat's schedule in the ${brandLabel} app to stay in future events.`
}

/**
 * Decides which schedule-conflict warning (if any) to show for a thermostat's DR event, from the
 * event + thermostat the API returns. Pure so the behavior -- which API state maps to which
 * message -- is unit-testable without hooks/Redux. Two flavors:
 *  - "did opt out": the schedule already opted the member out (`cancelled_reason` flag from the BE).
 *  - "will opt out": still enrolled, but the schedule will opt them out mid-event
 *    (`schedule_opt_out_datetime` from the BE; only set for an active, non-cancelled ecobee/Honeywell
 *    event -- never Nest).
 * Returns null when there's nothing to warn about.
 */
export function resolveScheduleOptOutWarning(
  event: ThermostatDemandResponseEvent | undefined,
  thermostat: Thermostat | undefined,
): string | null {
  if (!event) {
    return null
  }

  const brandLabel = thermostat?.portal ?? 'thermostat'

  // The schedule has already opted them out of the current event.
  if (event.cancelled_reason === SCHEDULE_OPT_OUT_CANCELLED_REASON) {
    return buildScheduleDidOptOutWarning(brandLabel)
  }

  // Still enrolled, but the schedule is set to opt them out later in the event. The backend only
  // sets the field for an active event; guard defensively against a stale event in the store.
  if (event.enrolled && event.schedule_opt_out_datetime) {
    if (!eventIsCurrentlyActive(event)) {
      return null
    }
    return buildScheduleWillOptOutWarning({
      brandLabel,
      scheduleOptOutTime: getDateFromTimestamp(
        event.schedule_opt_out_datetime,
        'h:mm A',
      ),
    })
  }

  return null
}

/**
 * The schedule-conflict warning copy for a thermostat's DR event, or null when there's nothing to
 * warn about. Thin wrapper that reads the next event + thermostat from the store and delegates the
 * decision to {@link resolveScheduleOptOutWarning}.
 */
export function useScheduleOptOutWarning(thermostatId: ID): string | null {
  const nextEvent = useAppSelector((state) =>
    selectNextWithThermostatId(state, thermostatId),
  )
  const thermostat = useAppSelector((state) =>
    thermostatsCollection.selectors.selectById(state, thermostatId),
  )

  return resolveScheduleOptOutWarning(nextEvent, thermostat)
}
